import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAdminAuth } from '../contexts/AdminAuthContext';
import { MessageSquare,  
  LayoutDashboard, Users, MapPin, Wrench, Settings,
  Menu, X, Truck, LogOut, UserCog, ClipboardList, 
  Lock, AlertTriangle, ArrowUpRight, Clock, CreditCard, Zap, Hammer, DollarSign, Receipt, BarChart3, Bot
, Layers, FilePlus2, FileSpreadsheet, CalendarClock, Activity, Route
} from 'lucide-react';
import { useState, useEffect, useCallback } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter
} from '../../components/ui/dialog';
import { toast } from 'sonner';
import NotificationBell from './NotificationBell';

const FEATURE_MAP = {
  dashboard: { features: [], label: null },
  drivers: { features: ['carte_en_direct'], label: 'Localisation' },
  map: { features: ['carte_en_direct'], label: 'Localisation' },
  dispatch: { features: ['module_dispatch'], label: 'Repartition' },
  billing: { features: ['module_facturation'], label: 'Facturation' },
  expenses: { features: [], label: null },
  fleet: { features: ['gestion_flotte'], label: null },
  maintenance: { features: ['inspections_saaq'], label: null },
  'client-portal': { features: ['portail_clients'], label: 'Repartition' },
  settings: { features: [], label: null },
  'mecanicien': { features: [], label: null },
};

// ==================== SIDEBAR TRIAL BADGE ====================

const SidebarTrialBadge = ({ subscriptionStatus }) => {
  const navigate = useNavigate();

  if (subscriptionStatus.isExpired) {
    return (
      <div className="mx-4 mt-3 p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
        <div className="flex items-start gap-2">
          <AlertTriangle size={16} className="text-red-400 mt-0.5 shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-red-400 text-xs font-semibold">Abonnement expire</p>
            <p className="text-red-400/70 text-xs mt-0.5">Vos fonctionnalites sont restreintes.</p>
            <button
              onClick={() => navigate('/admin/settings')}
              className="text-red-400 text-xs font-semibold mt-1 flex items-center gap-1 hover:underline"
              data-testid="renew-subscription-btn"
            >
              Renouveler <ArrowUpRight size={12} />
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (subscriptionStatus.isTrialing && subscriptionStatus.trialDaysRemaining !== null) {
    const days = subscriptionStatus.trialDaysRemaining;
    const isUrgent = days <= 3;
    return (
      <div className={`mx-4 mt-3 p-3 rounded-lg border ${isUrgent ? 'bg-amber-500/10 border-amber-500/30' : 'bg-slate-800/50 border-slate-700'}`}>
        <div className="flex items-start gap-2">
          <AlertTriangle size={16} className={`${isUrgent ? 'text-amber-400' : 'text-slate-400'} mt-0.5 shrink-0`} />
          <div className="flex-1 min-w-0">
            <p className={`text-xs font-semibold ${isUrgent ? 'text-amber-400' : 'text-slate-300'}`}>
              Essai gratuit — {days} jour{days !== 1 ? 's' : ''} restant{days !== 1 ? 's' : ''}
            </p>
            <button
              onClick={() => navigate('/admin/settings')}
              className={`text-xs font-semibold mt-1 flex items-center gap-1 hover:underline ${isUrgent ? 'text-amber-400' : 'text-slate-400'}`}
              data-testid="upgrade-plan-btn"
            >
              Passer au plan payant <ArrowUpRight size={12} />
            </button>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

// ==================== TOP BANNER (3 days or less / expired) ====================

const TopUrgentBanner = ({ subscriptionStatus, onDismiss }) => {
  const navigate = useNavigate();
  const days = subscriptionStatus.trialDaysRemaining;

  if (subscriptionStatus.isExpired) {
    return (
      <div className="w-full bg-red-600 text-white px-4 py-2.5 flex items-center justify-between" data-testid="top-expired-banner">
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <AlertTriangle size={18} className="shrink-0" />
          <p className="text-sm font-medium">
            Votre essai gratuit est termine. Abonnez-vous pour continuer a utiliser ProPilote.
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0 ml-4">
          <button
            onClick={() => navigate('/admin/settings')}
            className="bg-white text-red-600 font-bold text-sm px-4 py-1.5 rounded-lg hover:bg-red-50 transition-colors flex items-center gap-1.5"
            data-testid="top-banner-subscribe-btn"
          >
            <CreditCard size={14} />
            S'abonner maintenant
          </button>
          <button onClick={onDismiss} className="text-white/70 hover:text-white ml-1" data-testid="top-banner-dismiss-btn">
            <X size={16} />
          </button>
        </div>
      </div>
    );
  }

  if (subscriptionStatus.isTrialing && days !== null && days <= 3) {
    return (
      <div className="w-full bg-gradient-to-r from-amber-600 to-amber-500 text-black px-4 py-2.5 flex items-center justify-between" data-testid="top-urgent-banner">
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <Clock size={18} className="shrink-0" />
          <p className="text-sm font-medium">
            {days === 0
              ? "Votre essai gratuit expire aujourd'hui !"
              : `Plus que ${days} jour${days !== 1 ? 's' : ''} d'essai gratuit — ne perdez pas vos donnees !`
            }
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0 ml-4">
          <button
            onClick={() => navigate('/admin/settings')}
            className="bg-black text-amber-500 font-bold text-sm px-4 py-1.5 rounded-lg hover:bg-slate-900 transition-colors flex items-center gap-1.5"
            data-testid="top-banner-upgrade-btn"
          >
            <Zap size={14} />
            Passer au plan payant
          </button>
          <button onClick={onDismiss} className="text-black/50 hover:text-black ml-1" data-testid="top-banner-dismiss-btn">
            <X size={16} />
          </button>
        </div>
      </div>
    );
  }

  return null;
};

// ==================== MODAL (once per session, 3 days or less) ====================

const TrialExpiryModal = ({ subscriptionStatus }) => {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const days = subscriptionStatus.trialDaysRemaining;

  useEffect(() => {
    if (!subscriptionStatus.isTrialing || days === null) return;
    if (days > 3) return;

    const key = `trial_modal_dismissed_${days <= 0 ? 'expired' : days}`;
    if (sessionStorage.getItem(key)) return;

    const timer = setTimeout(() => setOpen(true), 1500);
    return () => clearTimeout(timer);
  }, [subscriptionStatus.isTrialing, days]);

  const handleDismiss = () => {
    const key = `trial_modal_dismissed_${days <= 0 ? 'expired' : days}`;
    sessionStorage.setItem(key, '1');
    setOpen(false);
  };

  const handleUpgrade = () => {
    handleDismiss();
    navigate('/admin/settings');
  };

  if (!subscriptionStatus.isTrialing || days === null || days > 3) return null;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) handleDismiss(); }}>
      <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-md" data-testid="trial-expiry-modal">
        <DialogHeader className="text-center items-center">
          <div className="w-16 h-16 bg-amber-500/10 rounded-full flex items-center justify-center mx-auto mb-2">
            <Clock size={32} className="text-amber-500" />
          </div>
          <DialogTitle className="text-xl text-white">
            {days === 0
              ? "Votre essai expire aujourd'hui"
              : `Plus que ${days} jour${days !== 1 ? 's' : ''} d'essai`
            }
          </DialogTitle>
          <DialogDescription className="text-slate-400 text-sm mt-2">
            {days === 0
              ? "Abonnez-vous maintenant pour continuer a utiliser ProPilote sans interruption."
              : "Ne perdez pas l'acces a vos donnees et fonctionnalites. Choisissez un plan adapte a vos besoins."
            }
          </DialogDescription>
        </DialogHeader>

        <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-4 my-2">
          <p className="text-slate-300 text-sm font-medium mb-2">Avec votre abonnement, vous gardez :</p>
          <ul className="space-y-1.5 text-sm text-slate-400">
            <li className="flex items-center gap-2"><span className="text-green-400">&#10003;</span> Toutes vos donnees de flotte et mesures</li>
            <li className="flex items-center gap-2"><span className="text-green-400">&#10003;</span> Inspections mécaniques et rapports PDF</li>
            <li className="flex items-center gap-2"><span className="text-green-400">&#10003;</span> Alertes email automatiques</li>
            <li className="flex items-center gap-2"><span className="text-green-400">&#10003;</span> Historique et graphiques de tendance</li>
          </ul>
        </div>

        <DialogFooter className="flex-col gap-2 sm:flex-col">
          <button
            onClick={handleUpgrade}
            className="w-full bg-amber-500 hover:bg-amber-400 text-black font-bold py-3 rounded-lg transition-colors flex items-center justify-center gap-2"
            data-testid="modal-upgrade-btn"
          >
            <CreditCard size={18} />
            Voir les plans et s'abonner
          </button>
          <button
            onClick={handleDismiss}
            className="w-full text-slate-500 hover:text-slate-300 text-sm py-2 transition-colors"
            data-testid="modal-dismiss-btn"
          >
            Me rappeler plus tard
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ==================== TOAST ON LOGIN (7 days or less) ====================

const useTrialLoginToast = (subscriptionStatus) => {
  const showToast = useCallback(() => {
    if (!subscriptionStatus.isTrialing) return;
    const days = subscriptionStatus.trialDaysRemaining;
    if (days === null || days > 7) return;

    const toastKey = `trial_toast_shown_${new Date().toDateString()}`;
    if (sessionStorage.getItem(toastKey)) return;
    sessionStorage.setItem(toastKey, '1');

    if (days <= 0) {
      toast.error("Votre essai gratuit expire aujourd'hui", {
        description: "Abonnez-vous pour ne pas perdre l'acces.",
        duration: 8000,
      });
    } else if (days <= 3) {
      toast.warning(`Plus que ${days} jour${days !== 1 ? 's' : ''} d'essai gratuit`, {
        description: "Pensez a vous abonner pour garder vos donnees.",
        duration: 7000,
      });
    } else {
      toast.info(`Essai gratuit : ${days} jours restants`, {
        description: "Profitez de toutes les fonctionnalites pendant votre essai.",
        duration: 5000,
      });
    }
  }, [subscriptionStatus.isTrialing, subscriptionStatus.trialDaysRemaining]);

  useEffect(() => {
    const timer = setTimeout(showToast, 2500);
    return () => clearTimeout(timer);
  }, [showToast]);
};

// ==================== MAIN LAYOUT ====================

const Layout = () => {
  const { user, organization, logout, hasModule, hasPage, hasFeature, isAtelier, isMechanic, isAgentVentes, isServiceClient, subscriptionStatus } = useAdminAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [bannerDismissed, setBannerDismissed] = useState(() => !!sessionStorage.getItem('top_banner_dismissed'));
  const location = useLocation();

  useTrialLoginToast(subscriptionStatus);

  const mechanicNavigation = [
    { name: 'Mécanicien', href: '/admin/mecanicien', icon: Hammer, key: 'mecanicien', pageKey: 'page_atelier', always: true, mechanicAccess: true },
    { name: 'Flotte', href: '/admin/fleet', icon: Truck, key: 'fleet', pageKey: 'page_flotte', module: 'module_entretiens', mechanicAccess: true },
    { name: 'Entretiens', href: '/admin/maintenance', icon: Wrench, key: 'maintenance', pageKey: 'page_entretiens', module: 'module_entretiens', mechanicAccess: true },
    { name: 'Parametres', href: '/admin/settings', icon: Settings, key: 'settings', pageKey: 'page_settings', always: true, mechanicAccess: true },
  ];

  const allNavigation = [
    { name: 'Tableau de bord', href: '/admin/dashboard', icon: LayoutDashboard, key: 'dashboard', pageKey: 'page_dashboard', always: true, hideForAtelier: true },
    { name: 'Chauffeurs', href: '/admin/drivers', icon: Users, key: 'drivers', pageKey: 'page_drivers', module: 'module_entretiens', hideForAtelier: true },
    { name: 'Sante GPS', href: '/admin/gps-health', icon: Activity, key: 'gps-health', pageKey: 'page_drivers', hideForAtelier: true },
    { name: 'Carte', href: '/admin/map', icon: MapPin, key: 'map', pageKey: 'page_map', module: 'module_entretiens', hideForAtelier: true },
    { name: 'Nouvelle commande', href: '/admin/agent-ventes', icon: FilePlus2, key: 'agent-ventes', pageKey: 'page_agent_ventes', module: 'module_logistique_plus', hideForAtelier: true, agentVentesAccess: true },
    { name: 'SAC · À planifier', href: '/admin/service-client/a-planifier', icon: Layers, key: 'sac-a-planifier', pageKey: 'page_service_client', module: 'module_logistique_plus', hideForAtelier: true },
    { name: 'SAC · RDV planifiés', href: '/admin/service-client/planifies', icon: Layers, key: 'sac-planifies', pageKey: 'page_service_client', module: 'module_logistique_plus', hideForAtelier: true },
    { name: 'SAC · Attribuées', href: '/admin/service-client/attribuees', icon: Layers, key: 'sac-attribuees', pageKey: 'page_service_client', module: 'module_logistique_plus', hideForAtelier: true },
    { name: 'Route Optimisation', href: '/admin/route-optimisation', icon: Route, key: 'route-optimisation', pageKey: 'page_dispatch', module: 'module_repartition', hideForAtelier: true },
    { name: 'Grille Commandes', href: '/admin/commandes', icon: FileSpreadsheet, key: 'commandes', pageKey: 'page_dispatch', module: 'module_repartition', hideForAtelier: true },
    { name: 'Planning Chauffeurs', href: '/admin/planning-chauffeurs', icon: CalendarClock, key: 'planning-chauffeurs', pageKey: 'page_dispatch', module: 'module_repartition', hideForAtelier: true },
    { name: 'Flotte', href: '/admin/fleet', icon: Truck, key: 'fleet', pageKey: 'page_flotte', module: 'module_entretiens', atelierAccess: true },
    { name: 'Entretiens', href: '/admin/maintenance', icon: Wrench, key: 'maintenance', pageKey: 'page_entretiens', module: 'module_entretiens', atelierAccess: true },
    { name: 'Atelier', href: '/admin/atelier', icon: Hammer, key: 'atelier', pageKey: 'page_atelier', module: 'module_entretiens', atelierAccess: true },
    { name: 'Mécanicien', href: '/admin/mecanicien', icon: Wrench, key: 'mecanicien', pageKey: 'page_atelier', module: 'module_entretiens', atelierAccess: true },
    { name: 'Portail Clients', href: '/admin/client-portal', icon: UserCog, key: 'client-portal', pageKey: 'page_portail_clients', module: 'module_repartition', hideForAtelier: true },
    { name: 'Facturation', href: '/admin/billing', icon: DollarSign, key: 'billing', pageKey: 'page_facturation', module: 'module_facturation', hideForAtelier: true },
    { name: 'Comptabilite', href: '/admin/expenses', icon: Receipt, key: 'expenses', pageKey: 'page_comptabilite', module: 'module_comptabilite', hideForAtelier: true, alwaysFallback: true },
    { name: 'Analytics', href: '/admin/analytics', icon: BarChart3, key: 'analytics', pageKey: 'page_analytics', always: true, hideForAtelier: true },
    { name: 'Rate-Agent IA', href: '/admin/rate-agent', icon: Bot, key: 'rate-agent', always: true, hideForAtelier: true, superAdminOnly: true },
    { name: 'Parametres', href: '/admin/settings', icon: Settings, key: 'settings', pageKey: 'page_settings', always: true, atelierAccess: true },
  ];

  const agentVentesNavigation = [
    { name: 'Nouvelle commande', href: '/admin/agent-ventes', icon: FilePlus2, key: 'agent-ventes', always: true },
    { name: 'Parametres', href: '/admin/settings', icon: Settings, key: 'settings', always: true },
  ];

  const serviceClientNavigation = [
    { name: 'SAC · À planifier', href: '/admin/service-client/a-planifier', icon: Layers, key: 'sac-a-planifier', always: true },
    { name: 'SAC · RDV planifiés', href: '/admin/service-client/planifies', icon: Layers, key: 'sac-planifies', always: true },
    { name: 'SAC · Attribuées', href: '/admin/service-client/attribuees', icon: Layers, key: 'sac-attribuees', always: true },
    { name: 'Parametres', href: '/admin/settings', icon: Settings, key: 'settings', always: true },
  ];

  const navigation = isMechanic
    ? mechanicNavigation.filter(item => !item.pageKey || hasPage(item.pageKey))
    : isAtelier
    ? allNavigation.filter(item => item.atelierAccess && (!item.pageKey || hasPage(item.pageKey)))
    : (isAgentVentes || isServiceClient)
    ? allNavigation.filter(item => {
        // V10 : filtre uniquement par hasPage (le patron coche = l'employe voit)
        if (!item.pageKey) return false;
        if (item.superAdminOnly && user?.role !== 'super_admin') return false;
        return hasPage(item.pageKey);
      })
    : allNavigation.filter(item =>
        (!item.hideForAtelier || !isAtelier) &&
        (!item.superAdminOnly || user?.role === 'super_admin') &&
        (!item.pageKey || hasPage(item.pageKey))
      );

  const isActive = (href) => {
    if (href === '/admin/mecanicien' && location.pathname.startsWith('/admin/mecanicien')) return true;
    if (href === '/admin/atelier' && location.pathname.startsWith('/admin/atelier')) return true;
    return location.pathname === href;
  };

  const canAccessItem = (item) => {
    if (item.always) return true;
    // V11 : Employe interne avec permission page-level = acces autorise (le patron a coche)
    const isInternalUser = ['service_client', 'agent_ventes', 'admin_entretiens', 'admin_repartition', 'admin_facturation', 'mechanic', 'atelier'].includes(user?.role);
    if (isInternalUser && item.pageKey && hasPage(item.pageKey)) return true;
    if (!hasModule(item.module)) return false;
    const fm = FEATURE_MAP[item.key];
    if (!fm || fm.features.length === 0) return true;
    return fm.features.every(f => hasFeature(f));
  };

  const getLockedLabel = (item) => {
    const fm = FEATURE_MAP[item.key];
    return fm?.label || 'Pro';
  };

  const handleBannerDismiss = () => {
    sessionStorage.setItem('top_banner_dismissed', '1');
    setBannerDismissed(true);
  };

  const showTopBanner = !bannerDismissed && (
    subscriptionStatus.isExpired || 
    (subscriptionStatus.isTrialing && subscriptionStatus.trialDaysRemaining !== null && subscriptionStatus.trialDaysRemaining <= 3)
  );

  return (
    <div className="min-h-screen bg-slate-950 flex flex-col">
      {/* Top urgent banner */}
      {showTopBanner && (
        <TopUrgentBanner subscriptionStatus={subscriptionStatus} onDismiss={handleBannerDismiss} />
      )}

      <div className="flex-1 flex">
        {sidebarOpen && (
          <div 
            className="fixed inset-0 bg-black/50 z-40 lg:hidden"
            onClick={() => setSidebarOpen(false)}
          />
        )}

        <aside className={`
          fixed lg:static inset-y-0 left-0 z-50
          w-64 bg-slate-900 border-r border-slate-800
          transform transition-transform duration-200 ease-in-out
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        `}>
          <div className="h-16 flex items-center justify-between px-4 border-b border-slate-800">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg overflow-hidden flex items-center justify-center bg-slate-900 ring-1 ring-amber-500/30">
                <img src="/icons/icon-192.png" alt="ProPilote" className="w-full h-full object-cover" />
              </div>
              <div>
                <p className="text-white font-bold">ProPilote</p>
                <p className="text-xs text-slate-400">{isMechanic ? 'Mecanicien' : isAtelier ? 'Atelier' : 'Admin'}</p>
              </div>
            </div>
            <button 
              onClick={() => setSidebarOpen(false)}
              className="lg:hidden text-slate-400 hover:text-white"
            >
              <X size={24} />
            </button>
          </div>

          <SidebarTrialBadge subscriptionStatus={subscriptionStatus} />

          <nav className="p-4 space-y-1 pb-32" data-testid="sidebar-nav">
            {navigation.map((item) => {
              const accessible = canAccessItem(item);
              return (
                <NavLink
                  key={item.name}
                  to={accessible ? item.href : '#'}
                  onClick={(e) => {
                    if (!accessible) e.preventDefault();
                    setSidebarOpen(false);
                  }}
                  className={`
                    flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors
                    ${isActive(item.href) 
                      ? 'bg-amber-500/20 text-amber-500' 
                      : accessible 
                        ? 'text-slate-400 hover:text-white hover:bg-slate-800'
                        : 'text-slate-600 cursor-not-allowed'
                    }
                  `}
                  data-testid={`nav-${item.key}`}
                >
                  <item.icon size={20} />
                  <span className="font-medium">{item.name}</span>
                  {!accessible && (
                    <span className="ml-auto flex items-center gap-1 text-xs bg-slate-800 text-slate-500 px-1.5 py-0.5 rounded">
                      <Lock size={10} />
                      {getLockedLabel(item)}
                    </span>
                  )}
                </NavLink>
              );
            })}
          </nav>

          <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-slate-800">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-slate-800 rounded-full flex items-center justify-center text-amber-500 font-bold">
                {user?.first_name?.[0]}{user?.last_name?.[0]}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-white text-sm font-medium truncate">
                  {user?.first_name} {user?.last_name}
                </p>
                <p className="text-slate-500 text-xs truncate">{organization?.name}</p>
              </div>
            </div>
            <button
              onClick={logout}
              className="w-full flex items-center justify-center gap-2 text-slate-400 hover:text-red-400 py-2 text-sm transition-colors"
              data-testid="logout-btn"
            >
              <LogOut size={16} />
              Deconnexion
            </button>
          </div>
        </aside>

        <div className="flex-1 flex flex-col min-h-0 min-w-0">
          <header className="h-16 bg-slate-900/50 border-b border-slate-800 flex items-center px-4 lg:px-6">
            <button
              onClick={() => setSidebarOpen(true)}
              className="lg:hidden text-slate-400 hover:text-white mr-4"
            >
              <Menu size={24} />
            </button>
            <div className="flex-1">
              <p className="text-slate-400 text-sm hidden md:block">
                {organization?.name || 'ProPilote Admin'}
              </p>
            </div>
            <NotificationBell />
          </header>

          <main className="flex-1 overflow-auto">
            <Outlet />
          </main>
        </div>
      </div>

      {/* Trial expiry modal (once per session) */}
      <TrialExpiryModal subscriptionStatus={subscriptionStatus} />
    </div>
  );
};

export default Layout;
